import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
	PlusIcon,
	Loader2Icon,
	Trash2,
	MoreVertical,
	Edit,
	CloudOff,
	Cloud,
	Copy,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import NoFormsFound from "./components/NoFormsFound";
import { useFormBuilder } from "./hooks/useFormBuilder";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogFooter,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogCancel,
	AlertDialogAction,
} from "../../components/ui/alert-dialog";
import { FormTemplatesSection } from "./components/form-templates-section.component";

export const FormsList: React.FC = () => {
	const {
		forms,
		isLoading,
		createNewForm,
		deleteForm,
		duplicateForm,
		isLastForm,
		togglePublishState,
	} = useFormBuilder();
	const navigate = useNavigate();
	const [formToDelete, setFormToDelete] = useState<string | null>(null);

	const { publishedForms, unpublishedForms } = useMemo(() => {
		const allForms = Object.values(forms);
		return {
			publishedForms: allForms.filter((form) => form.published_at),
			unpublishedForms: allForms.filter((form) => !form.published_at),
		};
	}, [forms]);

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<Loader2Icon className="h-8 w-8 animate-spin text-gray-400" />
			</div>
		);
	}

	if (Object.keys(forms).length === 0) {
		return (
			<div className="container mx-auto p-4 md:p-8 max-w-5xl">
				<FormTemplatesSection formCount={0} />
				<NoFormsFound onCreateNewForm={createNewForm} />
			</div>
		);
	}

	const handleConfirmDelete = () => {
		if (formToDelete) {
			deleteForm(formToDelete);
			setFormToDelete(null);
		}
	};

	const FormListItem = ({ form }: { form: (typeof publishedForms)[0] }) => (
		<div className="flex items-center justify-between p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors">
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-3 mb-1">
					<h3 className="font-medium text-gray-900 truncate">
						{form.title}
					</h3>
					{form.published_at && (
						<div className="flex items-center flex-shrink-0">
							<div className="w-2 h-2 bg-green-500 rounded-full mr-1.5" />
							<span className="text-green-600 text-xs font-medium">
								Veröffentlicht
							</span>
						</div>
					)}
				</div>
				<p className="text-sm text-gray-600 truncate mb-2">
					{form.description || "Keine Beschreibung"}
				</p>
				<div className="flex items-center gap-4 text-xs text-gray-500">
					<span>
						Zuletzt bearbeitet:{" "}
						{new Date(form.modified_at).toLocaleDateString(
							"de-DE",
							{
								day: "2-digit",
								month: "2-digit",
								year: "numeric",
							}
						)}
					</span>
					<span>
						{form.definition.sections.length}{" "}
						{form.definition.sections.length === 1
							? "Abschnitt"
							: "Abschnitte"}
					</span>
				</div>
			</div>
			<div className="flex items-center gap-2 ml-4 flex-shrink-0">
				<Button
					variant="outline"
					size="sm"
					onClick={() => navigate(`/auftragsformulare/${form.id}`)}
					className="gap-2"
					aria-label={`${form.title} bearbeiten`}
				>
					<Edit className="h-4 w-4" />
					Bearbeiten
				</Button>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							variant="ghost"
							size="icon"
							className="h-8 w-8"
							aria-label={`Optionen für ${form.title}`}
							data-testid={`form-options-menu-${form.id}`}
						>
							<MoreVertical className="h-4 w-4" />
							<span className="sr-only">Optionen</span>
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						{form.published_at ? (
							<DropdownMenuItem
								className="cursor-pointer"
								onClick={() => togglePublishState(form.id)}
								aria-label={`${form.title} zurückziehen`}
								data-testid={`unpublish-form-${form.id}`}
							>
								<CloudOff className="mr-2 h-4 w-4" />
								Zurückziehen
							</DropdownMenuItem>
						) : (
							<DropdownMenuItem
								className="cursor-pointer"
								onClick={() => togglePublishState(form.id)}
								aria-label={`${form.title} veröffentlichen`}
								data-testid={`publish-form-${form.id}`}
							>
								<Cloud className="mr-2 h-4 w-4" />
								Veröffentlichen
							</DropdownMenuItem>
						)}
						<DropdownMenuItem
							className="cursor-pointer"
							onClick={() => duplicateForm(form.id)}
							aria-label={`${form.title} duplizieren`}
							data-testid={`duplicate-form-${form.id}`}
						>
							<Copy className="mr-2 h-4 w-4" />
							Duplizieren
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							className="text-destructive focus:bg-destructive focus:text-destructive-foreground cursor-pointer"
							disabled={isLastForm}
							onClick={() => setFormToDelete(form.id)}
							aria-label={`${form.title} löschen`}
							data-testid={`delete-form-${form.id}`}
						>
							<Trash2 className="mr-2 h-4 w-4" />
							Löschen
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</div>
	);

	return (
		<div className="container mx-auto p-4 md:p-8 max-w-5xl">
			<div className="flex items-center justify-between mb-6">
				<h1 className="text-2xl font-bold text-gray-900">
					Auftragsformulare
				</h1>
				<Button
					onClick={createNewForm}
					className="flex items-center gap-2"
					aria-label="Neues Formular erstellen"
					data-testid="create-new-form-button"
				>
					<PlusIcon className="h-4 w-4" />
					Neues Formular
				</Button>
			</div>

			<FormTemplatesSection formCount={Object.keys(forms).length} />

			{publishedForms.length > 0 && (
				<div className="mb-8">
					<h2 className="text-lg font-semibold text-gray-700 mb-3">
						Veröffentlicht
					</h2>
					<div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
						{publishedForms.map((form) => (
							<FormListItem key={form.id} form={form} />
						))}
					</div>
				</div>
			)}

			{unpublishedForms.length > 0 && (
				<div>
					<h2 className="text-lg font-semibold text-gray-700 mb-3">
						Entwürfe
					</h2>
					<div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
						{unpublishedForms.map((form) => (
							<FormListItem key={form.id} form={form} />
						))}
					</div>
				</div>
			)}

			<AlertDialog
				open={formToDelete !== null}
				onOpenChange={(open) => !open && setFormToDelete(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Formular löschen</AlertDialogTitle>
						<AlertDialogDescription>
							Möchten Sie dieses Formular wirklich löschen? Diese
							Aktion kann nicht rückgängig gemacht werden.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Abbrechen</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleConfirmDelete}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							Löschen
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
};
